import { Box, Button, Paper, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import BPChart from "../Observation/BPChart";
import Pulse from "../Observation/Pulse";
import Temperature from "../Observation/Temp";
import Spo2 from "../Observation/Spo2";
import { store } from '../../redux/store/index';
import * as patientActions from '../../redux/actions/patientActions';
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import Scrollbar from "./Scrollbar";
import { CircularProgress, Grid } from '@mui/material';
import { socket } from "../../socket/socket";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import usePermissions from "../../utils/permissions";
import { useToast } from "../../context/ToastContext";
import {
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import WeightGainChart from "../Charts/WeightGainChart";
import FetalMovementGraph from "../Charts/DFMR";
import CommonGate from "./Gate";
import { preventNonDecimal, preventNonNumeric } from "../../utils/helper";
import FetalGrowthChartLocal from "../../components/FetalTracker/FetalGrowthChart";

const paperStyle = {
    sx: {
        width: {
            xs: "95%",
            sm: "100%",
            md: "100%",
            lg: "100%",
        },
    },
};

const inputStyles = {
    width: '45%',
    '& fieldset': { border: '1px solid #919191' },
    '& input': {
        fontSize: '12px',
        height: '10px',
        padding: '10px',
        backgroundColor: '#FFF',
        borderRadius: '3.45px',
        '&::placeholder': {
            fontSize: '11px',
            color: 'rgba(84, 84, 84, 1)',
            opacity: 1,
        },
    },
};

const gridStyle = {
    // backdropFilter: "blur(10px)",
    // WebkitBackdropFilter: "blur(10px)",
    border: "1px solid #E6E8EC",
    borderRadius: '6.9px',
    // backgroundColor: 'rgba(238, 251, 255, 0.3)',
    // boxShadow: '0px 4px 4px rgba(0,0,0,0.15)',
}

const CHART_HEIGHT = {
    xs: 260,
    md: 290,
};

const Vitals = () => {
    const { patientId, consultationId } = useParams();
    const patientReducer = useSelector(state => state.patientReducer);
    const vitals = patientReducer?.vitals
    const vitalsRange = patientReducer?.vitalsRange
    const [edit, setEdit] = useState(true)
    const location = useLocation();
    const [showSubmit, setShowSubmit] = useState(true)
    const tableRef = useRef(null);
    const [tableHeight, setTableHeight] = useState(0);
    const [loader, setLoader] = useState(true)
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { hasPermission } = usePermissions();
    const showToast = useToast()
    const [VitalsInput, setVitalsInput] = useState({
        temperatureUnit: 'C'
    })
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const [needsScroll, setNeedsScroll] = useState(false);
    const patientType = patientReducer?.patientInfo?.patientType || 'OBSTETRIC'
    const [submitState, setSubmitState] = useState({
        bp: "submit",
        pulse: "submit",
        temp: "submit",
        spo2: "submit",
    });

    const vitalsInputRef = useRef(VitalsInput);
    useEffect(() => {
        vitalsInputRef.current = VitalsInput;
    }, [VitalsInput]);

    useEffect(() => {
        const checkScroll = () => {
            if (!tableRef.current) return;

            setNeedsScroll(
                tableRef.current.scrollHeight > tableRef.current.clientHeight
            );
        };

        checkScroll();

        const observer = new ResizeObserver(checkScroll);

        if (tableRef.current) {
            observer.observe(tableRef.current);
        }

        window.addEventListener("resize", checkScroll);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", checkScroll);
        };
    }, [vitals, vitalsRange]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const mode = queryParams.get('mode');
        if (mode) {
            setEdit(mode == "view" ? false : true);
        }
    }, [location.search]);

    useEffect(() => {
        if (!patientId) return;
        const handleCommandOutput = (data) => {
            console.log("data came on event", data)
            const [, , rawCardType] = data?.data?.originActionType?.split('.');

            let cardType = rawCardType;
            let cardKey = rawCardType;
            if (rawCardType.includes(':')) {
                const [cardCategory, id] = rawCardType.split(':');
                cardType = cardCategory
                cardKey = rawCardType
            }
            if (consultationId !== data?.data?.consultationId) {
                return
            }
            if (
                data?.actionType === "commandOutput.refreshWebDashboard"
            ) {
                if (cardType == "pulseChart" || cardType == "spo2Chart" || cardType == "bpChart" || cardType == "temperatureChart") {
                    store.dispatch(patientActions.getPatientVitalsRequestAction(patientId));
                }
            }
        };
        socket.on("commandOutput", handleCommandOutput);
    }, [patientId]);

    useEffect(() => {
        const findConsultationDataPresent = vitals.find((item) => item.consultationId === consultationId)
        if (findConsultationDataPresent) {
            setVitalsInput(prev => {
                const newState = { ...prev, temperatureUnit: prev.temperatureUnit || "F" };
                if (!prev.diastolic && findConsultationDataPresent.diastolic) newState.diastolic = findConsultationDataPresent.diastolic;
                if (!prev.systolic && findConsultationDataPresent.systolic) newState.systolic = findConsultationDataPresent.systolic;
                if (!prev.pulse && findConsultationDataPresent.pulse) newState.pulse = findConsultationDataPresent.pulse;
                if (!prev.spo2 && findConsultationDataPresent.spo2) newState.spo2 = findConsultationDataPresent.spo2;
                if (!prev.temp && findConsultationDataPresent.temp) newState.temp = prev.temperatureUnit == "F" ? findConsultationDataPresent.temp : findConsultationDataPresent.tempCelsius;
                return newState;
            });

            setSubmitState(prev => {
                const newState = { ...prev };
                const currentInput = vitalsInputRef.current;

                if (findConsultationDataPresent.diastolic && findConsultationDataPresent.systolic) {
                    if ((currentInput.diastolic == findConsultationDataPresent.diastolic && currentInput.systolic == findConsultationDataPresent.systolic) || (!currentInput.diastolic && !currentInput.systolic)) {
                        newState.bp = "submitted";
                    }
                }
                if (findConsultationDataPresent.pulse) {
                    if (currentInput.pulse == findConsultationDataPresent.pulse || !currentInput.pulse) {
                        newState.pulse = "submitted";
                    }
                }
                if (findConsultationDataPresent.spo2) {
                    if (currentInput.spo2 == findConsultationDataPresent.spo2 || !currentInput.spo2) {
                        newState.spo2 = "submitted";
                    }
                }
                if (findConsultationDataPresent.temp) {
                    const serverTemp = currentInput.temperatureUnit == "F" ? findConsultationDataPresent.temp : findConsultationDataPresent.tempCelsius;
                    if (currentInput.temp == serverTemp || !currentInput.temp) {
                        newState.temp = "submitted";
                    }
                }
                return newState;
            });
            if (!edit) {
                setShowSubmit(false)
            }
        }
    }, [patientReducer?.vitals])



    useEffect(() => {
        if (patientId) {
            store.dispatch(patientActions.getPatientVitalsRequestAction(patientId));
            store.dispatch(patientActions.getPatientVitalsRangeRequestAction(patientId));
        }
    }, [patientId])


    const handleNumberChange = (field) => (e) => {
        const value = e.target.value;

        // Allow only digits
        if (/^\d*$/.test(value)) {
            changeVitalsInput(field, value);
        }
    };

    const handleDecimalChange = (field) => (e) => {
        let value = e.target.value;

        // Remove everything except digits and dot
        value = value.replace(/[^0-9.]/g, "");

        // Allow only one decimal point
        const parts = value.split(".");
        if (parts.length > 2) {
            value = parts[0] + "." + parts.slice(1).join("");
        }

        changeVitalsInput(field, value);
    };

    const changeVitalsInput = (field, value) => {
        setVitalsInput(prevState => {
            const updatedData = { ...prevState };
            if (value == "") {
                delete updatedData[field]
            }
            else {
                updatedData[field] = field === 'temp' ? value : parseInt(value)
            }
            return updatedData;
        });
        const vitalMap = {
            systolic: "bp",
            diastolic: "bp",
            pulse: "pulse",
            temp: "temp",
            spo2: "spo2",
        };

        if (vitalMap[field]) {
            setSubmitState(prev => ({
                ...prev,
                [vitalMap[field]]: "submit",
            }));
        }
    }

    const createVitals = (type) => {
        // const hasSystolic = !!VitalsInput.systolic;
        // const hasDiastolic = !!VitalsInput.diastolic;

        // if ((hasSystolic && !hasDiastolic) || (!hasSystolic && hasDiastolic)) {
        //     showToast(
        //         "Please fill both Systolic and Diastolic values",
        //         "info"
        //     );
        //     return;
        // }

        // // At least one vital should be present
        // if (Object.keys(VitalsInput).length === 1) {
        //     showToast("Fill at least one vital", "info");
        //     return;
        // }
        // // setShowSubmit(false)
        // store.dispatch(patientActions.createVitalsRequestAction(consultationId, VitalsInput))

        const payload = {};

        switch (type) {
            case "bp":
                if (!VitalsInput.systolic || !VitalsInput.diastolic) {
                    showToast("Please fill both Systolic and Diastolic values", "info");
                    return;
                }

                payload.systolic = VitalsInput.systolic;
                payload.diastolic = VitalsInput.diastolic;
                break;

            case "pulse":
                if (!VitalsInput.pulse) {
                    showToast("Please enter Pulse", "info");
                    return;
                }

                payload.pulse = VitalsInput.pulse;
                break;

            case "temp":
                if (!VitalsInput.temp) {
                    showToast("Please enter Temperature", "info");
                    return;
                }

                payload.temp = Number(VitalsInput.temp);
                payload.temperatureUnit = VitalsInput.temperatureUnit;
                break;

            case "spo2":
                if (!VitalsInput.spo2) {
                    showToast("Please enter SpO₂", "info");
                    return;
                }

                payload.spo2 = VitalsInput.spo2;
                break;
        }
        store.dispatch(
            patientActions.createVitalsRequestAction(
                consultationId,
                payload
            )
        );
    }

    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setTableHeight(entry.contentRect.height);
            }
        });
        if (tableRef.current) {
            observer.observe(tableRef.current);
        }
        return () => {
            if (tableRef.current) {
                observer.unobserve(tableRef.current);
            }
        };
    }, []);

    const renderSubmitButton = (field) => {
        return (edit && showSubmit && hasPermission('VITALS_CREATE')) && (
            <Button
                variant="contained"
                onClick={() => createVitals(field)}
                sx={{ height: '18px', minWidth: '70px', fontSize: '10px', backgroundColor: '#F27704', borderRadius: '13.8px' }}
                disabled={submitState[field] === "submitted"}
            >
                {submitState[field] === "submitted" ? "Submitted" : "Submit"}
            </Button>
        );
    }



    const renderContent = () => {
        return <Box ref={tableRef} sx={{ height: { xs: 'auto', md: '68vh', lg: "76vh", xl: '75vh' } }}>
            <CommonGate showComplaint={false} />
            {hasPermission('VITALS_VIEW') && <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Box sx={{ width: { xs: '100%', md: paperStyle.sx.width } }}>
                    <Grid container spacing={{ xs: 1, md: 2 }} sx={{ mb: showSubmit ? 0 : 1 }}>

                        <Grid size={{ xs: 12, xl: 4, lg: 6, md: 6 }} sx={gridStyle}>
                            <WeightGainChart />
                        </Grid>
                        {/* BP */}
                        <Grid size={{ xs: 12, xl: 4, lg: 6, md: 6 }} sx={gridStyle}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', pb: 1 }}>
                                <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>
                                    <BPChart
                                        data={vitals}
                                        systolicProjection={vitalsRange?.systolicBp || []}
                                        diastolicProjection={vitalsRange?.diastolicBp || []}
                                    />
                                </Box>
                                {(edit && hasPermission('VITALS_CREATE')) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, md: 4 }, mt: 0 }}>
                                        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
                                            <TextField
                                                placeholder="Diastolic"
                                                value={VitalsInput?.diastolic}
                                                disabled={!showSubmit}
                                                onChange={handleNumberChange("diastolic")}
                                                onKeyDown={preventNonNumeric}
                                                inputProps={{
                                                    maxLength: 3,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*",
                                                }}
                                                sx={{ ...inputStyles, width: '80px' }}
                                            />
                                            <TextField
                                                placeholder="Systolic"
                                                value={VitalsInput?.systolic}
                                                disabled={!showSubmit}
                                                onChange={handleNumberChange("systolic")}
                                                onKeyDown={preventNonNumeric}
                                                inputProps={{
                                                    maxLength: 3,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*",
                                                }}
                                                sx={{ ...inputStyles, width: '80px' }}
                                            />
                                        </Box>
                                        {renderSubmitButton("bp")}
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Pulse */}
                        <Grid size={{ xs: 12, xl: 4, lg: 6, md: 6 }} sx={gridStyle}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', pb: 1 }}>
                                <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>
                                    <Pulse
                                        data={vitals}
                                        projection={vitalsRange?.pulse || []}
                                    />
                                </Box>
                                {(edit && hasPermission('VITALS_CREATE')) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, md: 4 }, mt: 0 }}>
                                        <TextField
                                            placeholder="Pulse"
                                            value={VitalsInput?.pulse}
                                            disabled={!showSubmit}
                                            onChange={handleNumberChange("pulse")}
                                            onKeyDown={preventNonNumeric}
                                            inputProps={{
                                                maxLength: 3,
                                                inputMode: "numeric",
                                                pattern: "[0-9]*",
                                            }}
                                            sx={{ ...inputStyles, width: '80px' }}
                                        />
                                        {renderSubmitButton("pulse")}
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Temperature */}
                        <Grid size={{ xs: 12, xl: 4, lg: 6, md: 6 }} sx={gridStyle}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', pb: 1 }}>
                                <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>
                                    <Temperature
                                        data={vitals}
                                        projection={
                                            VitalsInput?.temperatureUnit === "F"
                                                ? vitalsRange?.temperaturs || []
                                                : vitalsRange?.temperaturesInCelcius || []
                                        }
                                        unit={VitalsInput?.temperatureUnit}
                                    />
                                </Box>
                                {(edit && hasPermission('VITALS_CREATE')) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, md: 4 }, mt: 0 }}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <TextField
                                                placeholder="Temp"
                                                value={VitalsInput?.temp}
                                                disabled={!showSubmit}
                                                onChange={handleDecimalChange("temp")}
                                                onKeyDown={preventNonDecimal}
                                                inputProps={{
                                                    maxLength: 5,
                                                    inputMode: "decimal",
                                                }}
                                                sx={{ ...inputStyles, width: '80px' }}
                                            />
                                            <FormControl
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#FFF",
                                                    borderRadius: "3.45px",
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                }}
                                            >
                                                <Select
                                                    value={VitalsInput?.temperatureUnit}
                                                    disabled={!showSubmit}
                                                    onChange={(e) => {
                                                        const newUnit = e.target.value;
                                                        setVitalsInput((prev) => {
                                                            let newTemp = prev.temp;
                                                            if (newTemp) {
                                                                const tempVal = parseFloat(newTemp);
                                                                if (newUnit === "C" && prev.temperatureUnit === "F") {
                                                                    newTemp = ((tempVal - 32) * 5 / 9).toFixed(1);
                                                                } else if (newUnit === "F" && prev.temperatureUnit === "C") {
                                                                    newTemp = ((tempVal * 9 / 5) + 32).toFixed(1);
                                                                }
                                                            }
                                                            return {
                                                                ...prev,
                                                                temperatureUnit: newUnit,
                                                                temp: newTemp
                                                            };
                                                        });
                                                    }}
                                                    sx={{
                                                        height: "30px",
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    <MenuItem value="C">°C</MenuItem>
                                                    <MenuItem value="F">°F</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        {renderSubmitButton("temp")}
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* SpO2 */}
                        <Grid size={{ xs: 12, xl: 4, lg: 6, md: 6 }} sx={gridStyle}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', pb: 1 }}>
                                <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>
                                    <Spo2
                                        data={vitals}
                                        projection={vitalsRange?.spo2 || []}
                                    />
                                </Box>
                                {(edit && hasPermission('VITALS_CREATE')) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, md: 4 }, mt: 0 }}>
                                        <TextField
                                            placeholder="Spo2"
                                            value={VitalsInput?.spo2}
                                            disabled={!showSubmit}
                                            onChange={handleNumberChange("spo2")}
                                            onKeyDown={preventNonNumeric}
                                            inputProps={{
                                                maxLength: 3,
                                                inputMode: "numeric",
                                                pattern: "[0-9]*",
                                            }}
                                            sx={{ ...inputStyles, width: '80px' }}
                                        />
                                        {renderSubmitButton("spo2")}
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {patientType !== "GYNECOLOGY" && <Grid size={{ xs: 12, xl: 4, lg: 6, md: 6 }} sx={gridStyle}>
                            <Box position="relative" sx={{
                                height: CHART_HEIGHT,
                            }}>
                                <Box sx={{ height: "calc(100% - 5px)" }}>
                                    <FetalMovementGraph title="DFMR" records={patientReducer?.dfmrChart} />
                                </Box>
                            </Box>
                        </Grid>}

                        {patientType !== "GYNECOLOGY" && <Grid size={{ xs: 12, xl: 5, lg: 6, md: 6 }} sx={gridStyle}>
                            <FetalGrowthChartLocal patientId={patientId} />
                        </Grid>}

                    </Grid>
                </Box>

            </Box>}
        </Box>
    }

    return (
        isMobile ? renderContent() : needsScroll ?
            <Scrollbar gridTemplate="auto / 1fr 14px" padding="0 1.2rem 1rem 1rem" scrollerPadding="0rem" scrollerWidth="12px">
                {renderContent()}
            </Scrollbar>
            : renderContent()
    );
};

export default Vitals;


